import { Box, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { JSX } from "react";

import { PageLoader } from "@/components/ui/page-loader";

import {
  salesApi,
  type SalesCompanyKnowledge,
  type SalesDataAccess,
  type SalesSkill,
} from "../client.js";
import { SalesErrorState } from "../components/SalesEmptyState.js";
import { devLog } from "../devLog.js";
import type { SxStyle } from "../leads/heatStyles.js";
import { CapabilityMap } from "./CapabilityMap.js";

/** Card surface (was the global `.panel`). */
const panelSx: SxStyle = {
  display: "grid",
  gap: 1.5,
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: (t: Theme) => t.shadows[1],
  p: 2.5,
};

/** Recessed sub-box (was the global `.panel-inset`). */
const panelInsetSx: SxStyle = {
  borderRadius: 1,
  border: 1,
  borderColor: "divider",
  bgcolor: "surfaceSoft",
};

/**
 * Knowledge tab — transparency into what the autonomous agent can DO
 * (skills), can SEE (data access + guardrails it will NOT cross), and has
 * LEARNED about XOP (the company profile it distilled). Read-only; backed
 * by GET /api/sales/v2/capabilities.
 */
export function KnowledgePage(): JSX.Element {
  useEffect(() => devLog("knowledge/mount"), []);

  const cap = useQuery({
    queryKey: ["sales", "capabilities"],
    queryFn: () => salesApi.getCapabilities(),
    refetchInterval: 5 * 60_000,
  });

  if (cap.isLoading) {
    return <PageLoader />;
  }
  if (cap.isError) return <SalesErrorState error={cap.error} surface="data" />;
  if (!cap.data) return <Box />;

  const c = cap.data;
  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <SkillsSection skills={c.skills} />
      <CapabilityMap />
      <AccessSection
        data={c.data_access}
        cannot={c.cannot}
        crmEnabled={c.crm_directory_enabled}
      />
      <CompanySection company={c.company} />
    </Box>
  );
}

function Section({
  title, icon, subtitle, children,
}: {
  title: string;
  icon: string;
  subtitle?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box component="section" sx={panelSx}>
      <Box component="header" sx={{ display: "grid", gap: 0.25 }}>
        <Typography component="h3" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
          <Box component="span" aria-hidden>{icon}</Box>
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>{subtitle}</Typography>
        ) : null}
      </Box>
      {children}
    </Box>
  );
}

function SkillsSection({ skills }: { skills: SalesSkill[] }): JSX.Element {
  return (
    <Section
      title="Skills"
      icon="🛠"
      subtitle="What the agent can do on its own, every tick."
    >
      <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: { sm: "repeat(2, 1fr)" } }}>
        {skills.map((s) => (
          <Box key={s.name} sx={[panelInsetSx, { display: "grid", gap: 0.5, p: 1.5 }]}>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
              <Box component="span" sx={{ fontSize: 14, fontWeight: 600, color: "text.primary" }}>
                {s.name}
              </Box>
              <Box component="span" sx={{ borderRadius: 999, border: 1, borderColor: "divider", px: 1, py: 0.25, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
                {s.category}
              </Box>
            </Box>
            <Typography sx={{ fontSize: 12, lineHeight: 1.6, color: "text.secondary" }}>
              {s.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function AccessSection({
  data, cannot, crmEnabled,
}: {
  data: SalesDataAccess[];
  cannot: string[];
  crmEnabled: boolean;
}): JSX.Element {
  return (
    <Section
      title="Data access"
      icon="🔐"
      subtitle="What it can read — and the lines it will not cross."
    >
      <Box component="ul" sx={{ display: "grid", gap: 0.75, listStyle: "none", m: 0, p: 0 }}>
        {data.map((d) => (
          <Box
            component="li"
            key={d.source}
            sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1, rowGap: 0.25, fontSize: 14 }}
          >
            <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>
              {d.source}
            </Box>
            <Box component="span" sx={{ fontSize: 12, color: "text.disabled" }}>
              — {d.scope}
            </Box>
            {d.source.toLowerCase().includes("crm") ? (
              <Box
                component="span"
                sx={[
                  { borderRadius: 999, px: 1, py: 0.25, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },
                  crmEnabled
                    ? { bgcolor: (t: Theme) => alpha(t.palette.success.main, 0.15), color: "success.main" }
                    : { bgcolor: "surfaceSoft", color: "text.disabled" },
                ]}
              >
                {crmEnabled ? "connected" : "not connected"}
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 1,
          borderRadius: 1,
          border: 1,
          borderColor: (t: Theme) => alpha(t.palette.error.main, 0.3),
          bgcolor: (t: Theme) => alpha(t.palette.error.main, 0.04),
          p: 1.5,
        }}
      >
        <Box sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "error.main" }}>
          Will not do / cannot see
        </Box>
        <Box component="ul" sx={{ display: "grid", gap: 1, listStyle: "none", m: 0, p: 0 }}>
          {cannot.map((c) => (
            <Box
              component="li"
              key={c}
              sx={{ display: "flex", gap: 0.75, fontSize: 12, lineHeight: 1.6, color: "text.secondary" }}
            >
              <Box component="span" aria-hidden sx={{ color: "error.main" }}>
                ✕
              </Box>
              <Box component="span">{c}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Section>
  );
}

function ChipRow({ label, items }: { label: string; items: string[] }): JSX.Element | null {
  if (!items || items.length === 0) return null;
  return (
    <Box sx={{ display: "grid", gap: 0.5 }}>
      <Box sx={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {label}
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {items.map((it) => (
          <Box
            component="span"
            key={it}
            sx={{ borderRadius: 999, border: 1, borderColor: "divider", bgcolor: "background.default", px: 1, py: 0.25, fontSize: 12, color: "text.primary" }}
          >
            {it}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function CompanySection({
  company,
}: { company: SalesCompanyKnowledge | null }): JSX.Element {
  if (!company) {
    return (
      <Section title="What it knows about XOP" icon="🏢">
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
          Still learning — the agent hasn't built a company profile yet. It
          reads xopnetworks.com on boot and refreshes daily.
        </Typography>
      </Section>
    );
  }
  const isFallback = company.source_type === "fallback";
  const learned = company.learned_at
    ? new Date(company.learned_at).toLocaleString()
    : "—";
  return (
    <Section
      title="What it knows about XOP"
      icon="🏢"
      subtitle={`Profile v${company.version ?? "?"} · ${
        isFallback
          ? "built-in fallback (live site scrape pending)"
          : `${company.pages_ingested ?? 0} pages read`
      } · learned ${learned}`}
    >
      {isFallback ? (
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: (t: Theme) => alpha(t.palette.warning.main, 0.4),
            bgcolor: (t: Theme) => alpha(t.palette.warning.main, 0.06),
            p: 1,
            fontSize: 12,
            color: "warning.main",
          }}
        >
          Using the built-in fallback profile — the live xopnetworks.com scrape
          hasn't completed yet. The agent still operates on its persona
          knowledge; this upgrades automatically once the scrape succeeds.
        </Box>
      ) : null}
      {company.summary ? (
        <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: "text.primary" }}>
          {company.summary}
        </Typography>
      ) : null}
      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { sm: "repeat(2, 1fr)" } }}>
        <ChipRow label="Products" items={company.products} />
        <ChipRow label="Verticals" items={company.verticals} />
        <ChipRow label="Target buyers" items={company.target_buyers} />
        <ChipRow label="Geographies" items={company.geographies} />
      </Box>
      <ChipRow label="Differentiators" items={company.differentiators} />
    </Section>
  );
}
